import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Loader2, CheckCircle2, XCircle, AlertTriangle, TrendingUp,
  ChevronDown, ChevronUp, Target, Sparkles, ArrowRight, Key,
} from "lucide-react";

const STAGE_URL = "https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/tailor-resume-claude-jd";
const STAGE_APIKEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I";

export interface ClaudeDirectAnalysisInputs {
  organized_jd: string;
  evidence_map: string;
  original_resume: string;
  job_description: string;
  resume_json: unknown;
  tailored_resume_text: string;
  /** Provenance, echoed back by stage 4 (optional on older saved runs). */
  job_id?: string | null;
  job_title?: string | null;
  company_name?: string | null;
  original_resume_name?: string | null;
}

/** What stage 4 says it scored — shown under the donuts so the numbers are traceable. */
interface Provenance {
  job?: { id?: string | null; title?: string | null; company?: string | null; first_line?: string; chars?: number; sha256?: string; responsibilities?: number; requirements?: number; skills?: number };
  original_resume?: { name?: string | null; chars?: number; first_line?: string; sha256?: string };
  tailored_resume?: { name?: string; lines?: number; chars?: number; sha256?: string };
  method?: string;
  scored_at?: string;
}

type Where = string | string[] | undefined;
type Lvl = "full" | "partial" | "none";
interface Side { covered?: boolean; where?: Where; level?: Lvl }
interface PlacementItem { rank?: number; text?: string; original?: Side; tailored?: Side & { count?: number } }
interface CategoryScore { score?: number; matched?: number; total?: number; met?: number; partial?: number }

interface ProblemItem { their_problem?: string; your_solution?: string; where_it_appears?: string; what_changed?: string }
interface PositionChange { position?: string; changes?: string[] }
interface AnalysisReport {
  overview?: string;
  problems_you_now_solve?: { summary?: string; problems?: ProblemItem[] };
  what_changed?: {
    career_summary?: { before?: string; after?: string; problems_now_named?: string[]; metrics_highlighted?: string[] };
    career_highlights?: { before?: string; after?: string; new_micro_headings?: string[]; key_changes?: string[] };
    skills_section?: { skills_added?: string[]; skills_removed?: string[]; why_it_matters?: string };
    professional_experience?: { before?: string; after?: string; key_changes?: PositionChange[] };
  };
  keywords_added?: { summary?: string; coverage_percentage?: number; keywords_added?: string[]; keywords_still_missing?: string[] };
  why_this_matters?: { before?: string; after?: string };
}

interface AnalysisData {
  provenance?: Provenance;
  scores?: {
    before?: number; after?: number; improvement?: number;
    before_categories?: Record<string, CategoryScore>;
    after_categories?: Record<string, CategoryScore>;
  };
  summary?: { jd_breakdown_rationale?: string; what_was_done?: string };
  report?: AnalysisReport;
  placement?: { responsibilities?: PlacementItem[]; requirements?: PlacementItem[]; skills?: PlacementItem[] };
}

/** Strip em/en dashes from any displayed string (belt-and-suspenders with the server). */
function clean(s?: string): string {
  return String(s ?? "").replace(/\s*[—–]\s*/g, ", ").replace(/\s*,\s*,/g, ", ").trim();
}
function cleanList(arr?: string[]): string[] {
  return (arr ?? []).map((x) => clean(x)).filter(Boolean);
}

function whereToList(w: Where): string[] {
  if (!w) return [];
  if (Array.isArray(w)) return w.filter(Boolean).map((x) => clean(String(x)));
  const s = clean(String(w));
  return s && s.toLowerCase() !== "not present" ? [s] : [];
}

function levelOf(side?: Side): Lvl {
  const l = String(side?.level ?? "").toLowerCase();
  if (l === "full" || l === "partial" || l === "none") return l as Lvl;
  return side?.covered ? "full" : "none";
}

/* ------------------------------------------------------------------ */
/* Small building blocks                                               */
/* ------------------------------------------------------------------ */

function ScoreDonut({ label, score, color }: { label: string; score: number; color: string }) {
  return (
    <div className="flex flex-col items-center">
      <div
        className="relative w-28 h-28 rounded-full flex items-center justify-center"
        style={{ background: `conic-gradient(${color} ${score * 3.6}deg, #e5e7eb 0deg)` }}
      >
        <div className="absolute w-20 h-20 rounded-full bg-white flex flex-col items-center justify-center">
          <span className="text-2xl font-black text-slate-900">{score}%</span>
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Match</span>
        </div>
      </div>
      <span className="mt-2 text-sm font-bold text-slate-700">{label}</span>
    </div>
  );
}

/** Collapsible section shell so the report reads neatly. */
function Section({
  title, icon, defaultOpen = true, badge, children,
}: { title: string; icon: React.ReactNode; defaultOpen?: boolean; badge?: React.ReactNode; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors">
        <span className="flex items-center gap-2 font-bold text-slate-800">{icon}{title}</span>
        <span className="flex items-center gap-2">{badge}{open ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}</span>
      </button>
      {open && <div className="px-4 py-4">{children}</div>}
    </div>
  );
}

function Chips({ items, tone }: { items: string[]; tone: "green" | "red" | "blue" | "amber" }) {
  const cls = {
    green: "bg-emerald-100 text-emerald-800 border-emerald-200",
    red: "bg-rose-100 text-rose-800 border-rose-200",
    blue: "bg-blue-100 text-blue-800 border-blue-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
  }[tone];
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => <span key={i} className={`text-xs font-medium px-2 py-0.5 rounded-full border ${cls}`}>{it}</span>)}
    </div>
  );
}

/** Before / After side-by-side comparison TABLE — one shared frame, colored
 *  header row, both cells always the same height so the eye can compare. */
function BeforeAfter({ before, after }: { before?: string; after?: string }) {
  const b = clean(before), a = clean(after);
  if (!b && !a) return null;
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="grid grid-cols-2 divide-x divide-slate-200">
        <div className="bg-rose-50 px-3 py-2 text-[11px] font-black uppercase tracking-wider text-rose-700">Before</div>
        <div className="bg-emerald-50 px-3 py-2 text-[11px] font-black uppercase tracking-wider text-emerald-700">After</div>
      </div>
      <div className="grid grid-cols-2 divide-x divide-slate-200 border-t border-slate-200">
        <div className="p-3 bg-white"><p className="text-sm text-slate-700 leading-relaxed">{b || <span className="italic text-slate-400">Not present</span>}</p></div>
        <div className="p-3 bg-white"><p className="text-sm text-slate-700 leading-relaxed">{a || <span className="italic text-slate-400">Not present</span>}</p></div>
      </div>
    </div>
  );
}

/** Two lists side by side as a real table with aligned, striped rows —
 *  built for keyword/skill comparisons where scanning matters. */
function ListCompareTable({
  leftTitle, rightTitle, leftItems, rightItems, leftTone, rightTone,
}: {
  leftTitle: string; rightTitle: string; leftItems: string[]; rightItems: string[];
  leftTone: "green" | "red" | "amber"; rightTone: "green" | "red" | "amber";
}) {
  const tones = {
    green: { head: "bg-emerald-50 text-emerald-700", dot: "text-emerald-500" },
    red: { head: "bg-rose-50 text-rose-700", dot: "text-rose-500" },
    amber: { head: "bg-amber-50 text-amber-700", dot: "text-amber-500" },
  };
  const rows = Math.max(leftItems.length, rightItems.length);
  if (rows === 0) return null;
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="grid grid-cols-2 divide-x divide-slate-200">
        <div className={`px-3 py-2 text-[11px] font-black uppercase tracking-wider ${tones[leftTone].head}`}>{leftTitle} ({leftItems.length})</div>
        <div className={`px-3 py-2 text-[11px] font-black uppercase tracking-wider ${tones[rightTone].head}`}>{rightTitle} ({rightItems.length})</div>
      </div>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={`grid grid-cols-2 divide-x divide-slate-200 border-t border-slate-100 ${i % 2 ? "bg-slate-50/60" : "bg-white"}`}>
          <div className="px-3 py-1.5 text-sm text-slate-700 flex gap-1.5">
            {leftItems[i] ? <><span className={tones[leftTone].dot}>•</span><span>{leftItems[i]}</span></> : <span>&nbsp;</span>}
          </div>
          <div className="px-3 py-1.5 text-sm text-slate-700 flex gap-1.5">
            {rightItems[i] ? <><span className={tones[rightTone].dot}>•</span><span>{rightItems[i]}</span></> : <span>&nbsp;</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Side-by-side placement                                              */
/* ------------------------------------------------------------------ */

function SideBox({ title, lvl, where, noneText, count }: { title: string; lvl: Lvl; where: string[]; noneText: string; count?: number }) {
  const box = lvl === "full" ? "border-emerald-200 bg-emerald-50"
    : lvl === "partial" ? "border-amber-200 bg-amber-50"
    : "border-slate-200 bg-slate-50";
  const icon = lvl === "full" ? <CheckCircle2 size={13} className="text-emerald-600" />
    : lvl === "partial" ? <AlertTriangle size={13} className="text-amber-500" />
    : <XCircle size={13} className="text-slate-400" />;
  const labelColor = lvl === "full" ? "text-emerald-700" : lvl === "partial" ? "text-amber-700" : "text-slate-400";
  const badge = lvl === "partial" ? " · Partial" : "";
  const usedTimes = count && count > 1 ? ` · used ${count}x` : "";
  return (
    <div className={`rounded-lg border p-2.5 ${box}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <span className={`text-[11px] font-bold uppercase tracking-wide ${labelColor}`}>{title}{usedTimes}{badge}</span>
      </div>
      {lvl !== "none" && where.length > 0
        ? <ul className="space-y-0.5">{where.map((w, i) => <li key={i} className="text-xs text-slate-700">• {w}</li>)}</ul>
        : lvl === "partial"
          ? <p className="text-xs italic text-amber-600">Partially meets it</p>
          : <p className="text-xs italic text-slate-400">{noneText}</p>}
    </div>
  );
}

function ItemRow({ item, showCount }: { item: PlacementItem; showCount?: boolean }) {
  const orig = whereToList(item.original?.where);
  const tail = whereToList(item.tailored?.where);
  const count = showCount ? item.tailored?.count : undefined;
  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-slate-100 text-slate-700 text-xs font-bold flex items-center justify-center">
          {item.rank ?? "•"}
        </span>
        <p className="text-sm font-semibold text-slate-900 flex-1">{clean(item.text)}</p>
      </div>
      <div className="grid md:grid-cols-2 gap-2 mt-2 ml-8">
        <SideBox title="Original resume" lvl={levelOf(item.original)} where={orig} noneText="Not present" />
        <SideBox title="Tailored resume" lvl={levelOf(item.tailored)} where={tail} noneText="Not addressed" count={count} />
      </div>
    </div>
  );
}

function PlacementGroup({ title, items, showCount }: { title: string; items: PlacementItem[]; showCount?: boolean }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  const addedCount = items.filter((it) => levelOf(it.tailored) !== "none" && levelOf(it.original) === "none").length;
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors">
        <span className="font-bold text-slate-800">{title} <span className="text-slate-400 font-medium">({items.length})</span></span>
        <span className="flex items-center gap-2">
          {addedCount > 0 && <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">+{addedCount} newly added</span>}
          {open ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
        </span>
      </button>
      {open && <div className="px-4">{items.map((it, i) => <ItemRow key={i} item={it} showCount={showCount} />)}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main                                                                */
/* ------------------------------------------------------------------ */

export default function ClaudeDirectAnalysis({
  inputs,
  job_title,
  company_name,
  job_url,
  initialData,
  onData,
}: {
  inputs: ClaudeDirectAnalysisInputs;
  job_title?: string | null;
  company_name?: string | null;
  job_url?: string | null;
  /** A previously persisted analysis — renders instantly, no re-run. */
  initialData?: AnalysisData | null;
  /** Called once with the finished analysis so the caller can persist it. */
  onData?: (data: AnalysisData) => void;
}) {
  const [loading, setLoading] = useState(!initialData);
  const [progress, setProgress] = useState(0);
  const [data, setData] = useState<AnalysisData | null>(initialData ?? null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (initialData) return; // saved analysis supplied — nothing to fetch
    if (started.current) return;
    started.current = true;

    let progTimer: ReturnType<typeof setInterval> | null = null;
    const t0 = Date.now();
    progTimer = setInterval(() => {
      const elapsed = (Date.now() - t0) / 1000;
      setProgress(Math.min(95, Math.round(95 * (1 - Math.exp(-elapsed / 22)))));
    }, 400);

    (async () => {
      try {
        const session = await supabase.auth.getSession();
        const token = session.data.session?.access_token;
        if (!token) throw new Error("You are not signed in.");
        // A 5xx here is almost always a transient Claude API overload blip —
        // retry once after 5s before surfacing a failure.
        const runAnalysis = () => fetch(STAGE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, apikey: STAGE_APIKEY },
          body: JSON.stringify({ stage: 4, ...inputs }),
        });
        let res = await runAnalysis();
        if (!res.ok && res.status >= 500) {
          await new Promise((r) => setTimeout(r, 5000));
          res = await runAnalysis();
        }
        if (!res.ok) throw new Error(`Analysis failed (HTTP ${res.status})`);
        const j = await res.json();
        if (!j || !j.success) throw new Error(j?.error || "Analysis failed");
        setData(j as AnalysisData);
        onData?.(j as AnalysisData);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (progTimer) clearInterval(progTimer);
        setProgress(100);
        setLoading(false);
      }
    })();

    return () => { if (progTimer) clearInterval(progTimer); };
  }, [inputs]);

  const scores = data?.scores;
  const improvement = scores?.improvement ?? 0;
  const report = data?.report;
  const wc = report?.what_changed;
  const kw = report?.keywords_added;

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        {(job_title || company_name) && (
          <div className="mb-2">
            {job_url ? (
              <a href={job_url} target="_blank" rel="noopener noreferrer" className="group inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5 no-underline">
                {job_title && <span className="text-xl font-black text-slate-900 group-hover:text-blue-700 transition-colors leading-tight">{job_title}</span>}
                {company_name && <span className="text-base font-semibold text-blue-600 group-hover:text-blue-800 transition-colors">@ {company_name}</span>}
                <svg className="inline w-3.5 h-3.5 text-slate-400 group-hover:text-blue-600 transition-colors mb-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </a>
            ) : (
              <div className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                {job_title && <span className="text-xl font-black text-slate-900 leading-tight">{job_title}</span>}
                {company_name && <span className="text-base font-semibold text-blue-600">@ {company_name}</span>}
              </div>
            )}
          </div>
        )}
        <h2 className="text-lg font-black text-slate-900">Tailoring Analysis</h2>
        <p className="text-sm text-slate-500">How your resume was scored against the full job, what changed, and what the tailored version now covers.</p>
      </div>

      {loading && (
        <div className="px-6 py-10 flex flex-col items-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-4" />
          <p className="font-semibold text-slate-800">Calculating your tailoring analysis…</p>
          <p className="text-sm text-slate-500 mb-4">This can take 30 to 60 seconds. It scores your resume, breaks down the job, and maps every item.</p>
          <div className="w-full max-w-md h-2.5 rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <span className="mt-2 text-xs text-slate-400">{progress}%</span>
        </div>
      )}

      {!loading && error && (
        <div className="px-6 py-8">
          <div className="flex items-center gap-3 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <XCircle className="w-5 h-5 shrink-0" />
            <div><p className="font-semibold">Analysis unavailable</p><p className="text-sm">{clean(error)}</p></div>
          </div>
        </div>
      )}

      {!loading && data && (
        <div className="px-6 py-6 space-y-5">
          {/* 1. SCORES — Resume Match Analysis panel on navy background */}
          {scores && scores.before != null && scores.after != null && (
            <div className="rounded-2xl bg-[#0b1f3f] p-4 sm:p-6">
              <div className="rounded-2xl bg-white p-5 sm:p-6">
              {/* Panel header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#0b1f3f] flex items-center justify-center shrink-0">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-xl font-black text-slate-900">Resume Match Analysis</h3>
              </div>

              {/* Position / company / status strip */}
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-5 py-3 mb-5 flex flex-wrap items-center gap-x-10 gap-y-2">
                {job_title && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Position</div>
                    <div className="text-sm font-black text-slate-900">{job_title}</div>
                  </div>
                )}
                {company_name && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Company</div>
                    <div className="text-sm font-black text-slate-900">{company_name}</div>
                  </div>
                )}
                <div className="ml-auto">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 text-white text-xs font-bold px-3.5 py-1.5 shadow-sm">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Successfully Optimized!
                  </span>
                </div>
              </div>

              {/* Provenance: exactly what was scored. Shown only when stage 4
                  reported it (older saved analyses have no provenance). */}
              {data?.provenance && (
                <div className="rounded-xl border border-slate-200 bg-white px-5 py-3 mb-5 text-xs text-slate-600">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Scored against</div>
                  <div className="grid sm:grid-cols-3 gap-x-6 gap-y-1.5">
                    <div>
                      <span className="font-bold text-slate-800">Job: </span>
                      {clean(data.provenance.job?.title ?? data.provenance.job?.first_line ?? "")}
                      {data.provenance.job?.company ? ` @ ${clean(data.provenance.job.company)}` : ""}
                      <span className="text-slate-400"> · {data.provenance.job?.responsibilities ?? 0} responsibilities, {data.provenance.job?.requirements ?? 0} requirements, {data.provenance.job?.skills ?? 0} skills · {data.provenance.job?.chars ?? 0} chars</span>
                    </div>
                    <div>
                      <span className="font-bold text-slate-800">Original resume: </span>
                      {clean(data.provenance.original_resume?.name ?? data.provenance.original_resume?.first_line ?? "")}
                      <span className="text-slate-400"> · {data.provenance.original_resume?.chars ?? 0} chars</span>
                    </div>
                    <div>
                      <span className="font-bold text-slate-800">Tailored resume: </span>
                      {clean(data.provenance.tailored_resume?.name ?? "")}
                      <span className="text-slate-400"> · {data.provenance.tailored_resume?.lines ?? 0} lines scored · id {data.provenance.tailored_resume?.sha256?.slice(0, 8) ?? "?"}</span>
                    </div>
                  </div>
                  {data.provenance.method && <p className="mt-1.5 text-[11px] text-slate-400">{data.provenance.method}</p>}
                </div>
              )}

              {/* Before / After / Boost cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* BEFORE */}
                <div className="relative rounded-2xl border-2 border-rose-200 bg-white pt-7 pb-5 px-4 text-center">
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-rose-600 text-white text-xs font-black tracking-wider px-5 py-1.5 shadow">BEFORE</span>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-rose-500 mb-3">Original Resume</div>
                  <div
                    className="relative w-28 h-28 mx-auto rounded-full flex items-center justify-center"
                    style={{ background: `conic-gradient(#e11d48 ${scores.before * 3.6}deg, #fde8ec 0deg)` }}
                  >
                    <div className="absolute w-[84px] h-[84px] rounded-full bg-white flex flex-col items-center justify-center">
                      <span className="text-2xl font-black text-slate-900">{scores.before}%</span>
                      <span className="text-[9px] uppercase tracking-wide text-slate-500">Match Rate</span>
                    </div>
                  </div>
                  <div className="mt-3 text-xs font-bold text-rose-600">
                    {scores.before >= 80 ? "Solid Start" : scores.before >= 65 ? "Room to Grow" : "Needs Improvement"}
                  </div>
                </div>

                {/* AFTER */}
                <div className="relative rounded-2xl border-2 border-emerald-200 bg-white pt-7 pb-5 px-4 text-center">
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-emerald-600 text-white text-xs font-black tracking-wider px-5 py-1.5 shadow">AFTER</span>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 mb-3">Tailored Resume</div>
                  <div
                    className="relative w-28 h-28 mx-auto rounded-full flex items-center justify-center"
                    style={{ background: `conic-gradient(#059669 ${scores.after * 3.6}deg, #d9f2e9 0deg)` }}
                  >
                    <div className="absolute w-[84px] h-[84px] rounded-full bg-white flex flex-col items-center justify-center">
                      <span className="text-2xl font-black text-slate-900">{scores.after}%</span>
                      <span className="text-[9px] uppercase tracking-wide text-slate-500">Match Rate</span>
                    </div>
                  </div>
                  <div className="mt-3 text-xs font-bold text-emerald-600">
                    {scores.after >= 90 ? "Excellent Match!" : scores.after >= 80 ? "Strong Match" : "Improved Match"}
                  </div>
                </div>

                {/* PERFORMANCE BOOST */}
                <div className="rounded-2xl border-2 border-amber-200 bg-white pt-5 pb-5 px-4 text-center flex flex-col">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-500 mb-3">Performance Boost</div>
                  <div className="flex-1 rounded-xl bg-[#0b1f3f] px-4 py-5 flex flex-col items-center justify-center">
                    <span className="text-4xl font-black text-amber-400 leading-none">{improvement >= 0 ? "+" : ""}{improvement}</span>
                    <span className="text-[10px] uppercase tracking-widest text-white/80 font-bold mt-1.5">Points Gained</span>
                    <div className="w-full mt-4">
                      <div className="h-2 rounded-full bg-white/15 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500" style={{ width: `${Math.min(100, scores.after)}%` }} />
                      </div>
                      <div className="text-[10px] text-white/70 font-semibold mt-1.5">Progress: {scores.after}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary band */}
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 px-5 py-4">
                <div className="flex items-center gap-2 font-black text-slate-900 text-sm mb-1">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  {improvement >= 25 ? "Outstanding Optimization Achieved!" : improvement >= 10 ? "Strong Optimization Achieved!" : "Optimization Complete"}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Your resume was strategically enhanced, lifting match compatibility from <strong className="text-slate-900">{scores.before}%</strong> to <strong className="text-slate-900">{scores.after}%</strong>, a <strong className="text-slate-900">{improvement >= 0 ? "+" : ""}{improvement} point improvement</strong> that
                  strengthens your candidacy{job_title ? <> for the <strong className="text-slate-900">{job_title}</strong> role</> : null}{company_name ? <> at <strong className="text-slate-900">{company_name}</strong></> : null}.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {["ATS Optimized", "Keyword Enhanced", "Performance Boosted"].map((b) => (
                    <span key={b} className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-[11px] font-bold px-3 py-1">
                      <CheckCircle2 className="w-3 h-3" /> {b}
                    </span>
                  ))}
                </div>
              </div>
              {scores.before_categories && scores.after_categories && (
                <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                  {(["keywords", "requirements", "responsibilities"] as const).map((k) => {
                    const b = scores.before_categories?.[k]?.score ?? 0;
                    const a = scores.after_categories?.[k]?.score ?? 0;
                    const cat = scores.after_categories?.[k];
                    const full = (cat?.matched ?? cat?.met);
                    const partial = cat?.partial;
                    const total = cat?.total;
                    const showBreakdown = total != null && total > 0;
                    const missing = showBreakdown ? Math.max(0, (total ?? 0) - (full ?? 0) - (partial ?? 0)) : 0;
                    return (
                      <div key={k} className="rounded-lg bg-slate-50 border border-slate-200 p-2">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 capitalize">{k}</div>
                        <div className="text-sm font-bold text-slate-800">{b}% to {a}%</div>
                        {showBreakdown && (
                          <div className="text-[10px] text-slate-500 mt-0.5">{full ?? 0} full · {partial ?? 0} partial · {missing} missing</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="mt-3 text-xs text-center text-slate-500 italic">Scored against the complete job description in your library.</p>
              </div>
            </div>
          )}

          {/* 2. OVERVIEW */}
          {clean(report?.overview) && (
            <div className="rounded-xl bg-blue-50/60 border border-blue-100 p-4">
              <h3 className="flex items-center gap-2 text-sm font-bold text-blue-900 mb-1"><Sparkles size={15} /> Transformation Overview</h3>
              <p className="text-sm text-slate-700 leading-relaxed">{clean(report?.overview)}</p>
            </div>
          )}

          {/* 3. WHY THIS MATTERS */}
          {report?.why_this_matters && (clean(report.why_this_matters.before) || clean(report.why_this_matters.after)) && (
            <Section title="Why This Matters" icon={<Target size={15} className="text-slate-500" />}>
              <BeforeAfter before={report.why_this_matters.before} after={report.why_this_matters.after} />
            </Section>
          )}

          {/* 4. PROBLEMS YOU NOW SOLVE */}
          {report?.problems_you_now_solve?.problems?.length ? (
            <Section title="Problems You Now Solve" icon={<CheckCircle2 size={15} className="text-emerald-600" />}>
              {clean(report.problems_you_now_solve.summary) && (
                <p className="text-sm text-slate-600 mb-3">{clean(report.problems_you_now_solve.summary)}</p>
              )}
              <div className="space-y-3">
                {report.problems_you_now_solve.problems!.map((p, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-3">
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 text-rose-500"><AlertTriangle size={15} /></span>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-900">{clean(p.their_problem)}</p>
                        {clean(p.your_solution) && (
                          <p className="mt-1 text-sm text-slate-700 flex gap-1"><ArrowRight size={14} className="mt-0.5 shrink-0 text-emerald-600" />{clean(p.your_solution)}</p>
                        )}
                        <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-slate-500">
                          {clean(p.where_it_appears) && <span>Appears in: <span className="font-medium text-slate-700">{clean(p.where_it_appears)}</span></span>}
                          {clean(p.what_changed) && <span>Change: <span className="font-medium text-slate-700">{clean(p.what_changed)}</span></span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          ) : null}

          {/* 5. WHAT CHANGED */}
          {wc && (wc.career_summary || wc.career_highlights || wc.skills_section || wc.professional_experience) && (
            <Section title="What Changed on Your Resume" icon={<Sparkles size={15} className="text-blue-600" />}>
              <div className="space-y-4">
                {wc.career_summary && (
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 mb-2">Career Summary</h4>
                    <BeforeAfter before={wc.career_summary.before} after={wc.career_summary.after} />
                    {cleanList(wc.career_summary.problems_now_named).length > 0 && (
                      <div className="mt-2"><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Problems now named</div><Chips items={cleanList(wc.career_summary.problems_now_named)} tone="blue" /></div>
                    )}
                    {cleanList(wc.career_summary.metrics_highlighted).length > 0 && (
                      <div className="mt-2"><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Metrics highlighted</div><Chips items={cleanList(wc.career_summary.metrics_highlighted)} tone="green" /></div>
                    )}
                  </div>
                )}
                {wc.career_highlights && (
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 mb-2">Career Highlights</h4>
                    <BeforeAfter before={wc.career_highlights.before} after={wc.career_highlights.after} />
                    {cleanList(wc.career_highlights.new_micro_headings).length > 0 && (
                      <div className="mt-2"><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">New micro-headings</div><Chips items={cleanList(wc.career_highlights.new_micro_headings)} tone="blue" /></div>
                    )}
                    {cleanList(wc.career_highlights.key_changes).length > 0 && (
                      <ul className="mt-2 space-y-1">{cleanList(wc.career_highlights.key_changes).map((c, i) => <li key={i} className="text-sm text-slate-700 flex gap-1.5"><span className="text-emerald-600">•</span>{c}</li>)}</ul>
                    )}
                  </div>
                )}
                {wc.skills_section && (
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 mb-2">Skills Section</h4>
                    <ListCompareTable
                      leftTitle="Skills Added"
                      rightTitle="Skills Removed"
                      leftItems={cleanList(wc.skills_section.skills_added)}
                      rightItems={cleanList(wc.skills_section.skills_removed)}
                      leftTone="green"
                      rightTone="red"
                    />
                    {clean(wc.skills_section.why_it_matters) && <p className="mt-2 text-sm text-slate-600 italic">{clean(wc.skills_section.why_it_matters)}</p>}
                  </div>
                )}
                {wc.professional_experience && (
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 mb-2">Professional Experience</h4>
                    <BeforeAfter before={wc.professional_experience.before} after={wc.professional_experience.after} />
                    {(wc.professional_experience.key_changes ?? []).length > 0 && (
                      <div className="mt-2 space-y-2">
                        {wc.professional_experience.key_changes!.map((pc, i) => (
                          <div key={i} className="rounded-lg border border-slate-200 p-2.5">
                            <div className="text-sm font-semibold text-slate-800">{clean(pc.position)}</div>
                            <ul className="mt-1 space-y-1">{cleanList(pc.changes).map((c, j) => <li key={j} className="text-sm text-slate-700 flex gap-1.5"><span className="text-emerald-600">•</span>{c}</li>)}</ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* 6. KEYWORDS */}
          {kw && (cleanList(kw.keywords_added).length > 0 || cleanList(kw.keywords_still_missing).length > 0) && (
            <Section
              title="Keyword Coverage"
              icon={<Key size={15} className="text-blue-600" />}
              badge={kw.coverage_percentage != null ? <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">{kw.coverage_percentage}% covered</span> : undefined}
            >
              {clean(kw.summary) && <p className="text-sm text-slate-600 mb-3">{clean(kw.summary)}</p>}
              {kw.coverage_percentage != null && (
                <div className="w-full h-2 rounded-full bg-slate-200 overflow-hidden mb-3">
                  <div className="h-full bg-blue-600" style={{ width: `${Math.max(0, Math.min(100, kw.coverage_percentage))}%` }} />
                </div>
              )}
              <ListCompareTable
                leftTitle="Keywords Added"
                rightTitle="Still Missing"
                leftItems={cleanList(kw.keywords_added)}
                rightItems={cleanList(kw.keywords_still_missing)}
                leftTone="green"
                rightTone="amber"
              />
            </Section>
          )}

          {/* 7. ITEM-BY-ITEM PLACEMENT */}
          {data.placement && (data.placement.skills?.length || data.placement.responsibilities?.length || data.placement.requirements?.length) ? (
            <Section title="Item-by-Item Coverage" icon={<CheckCircle2 size={15} className="text-slate-500" />} defaultOpen={false}>
              <div className="space-y-3">
                <PlacementGroup title="Skills / Keywords" items={data.placement?.skills ?? []} showCount />
                <PlacementGroup title="Responsibilities" items={data.placement?.responsibilities ?? []} />
                <PlacementGroup title="Requirements" items={data.placement?.requirements ?? []} />
              </div>
            </Section>
          ) : null}

          {/* 8. HOW THE JOB WAS BROKEN DOWN */}
          {data.summary && (clean(data.summary.jd_breakdown_rationale) || clean(data.summary.what_was_done)) && (
            <Section title="How & Why the Job Was Broken Down" icon={<Target size={15} className="text-slate-500" />} defaultOpen={false}>
              <div className="space-y-3">
                {clean(data.summary.jd_breakdown_rationale) && (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <h4 className="text-sm font-bold text-slate-800 mb-1">Job breakdown</h4>
                    <p className="text-sm text-slate-700 leading-relaxed">{clean(data.summary.jd_breakdown_rationale)}</p>
                  </div>
                )}
                {clean(data.summary.what_was_done) && (
                  <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 p-3">
                    <h4 className="text-sm font-bold text-emerald-900 mb-1">What was done</h4>
                    <p className="text-sm text-slate-700 leading-relaxed">{clean(data.summary.what_was_done)}</p>
                  </div>
                )}
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
